import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, X, Shirt, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const ROLES = [
  ["bride", "Bride"], ["groom", "Groom"], ["bridesmaid", "Bridesmaid"],
  ["groomsman", "Groomsman"], ["maid_of_honor", "Maid of Honor"], ["best_man", "Best Man"],
  ["flower_girl", "Flower Girl"], ["ring_bearer", "Ring Bearer"], ["parent", "Parent"],
  ["officiant", "Officiant"], ["other", "Other"],
];
const GARMENTS = [["dress", "Dress"], ["tux", "Tux"], ["suit", "Suit"], ["shoes", "Shoes"], ["accessory", "Accessory"]];
const PAYMENT = [["unpaid", "Unpaid"], ["deposit", "Deposit"], ["paid", "Paid"]];

const empty = () => ({
  member_name: "", role: "bridesmaid", garment_type: "dress",
  size: "", shoe_size: "", color: "", vendor: "", designer: "",
  fitting_date: "", pickup_date: "", payment_status: "unpaid",
  total_cost: 0, paid_amount: 0, notes: "",
});

export default function DressTuxPage() {
  const [items, setItems] = useState([]);
  const [guests, setGuests] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [busy, setBusy] = useState(false);

  async function load() {
    const [{ data: outfits }, w] = await Promise.all([
      api.get("/outfits"),
      api.get("/weddings/mine"),
    ]);
    setItems(outfits);
    if (w.data?.wedding_id) {
      try {
        const { data: g } = await api.get(`/weddings/${w.data.wedding_id}/guests`);
        setGuests(g);
      } catch {}
    }
  }
  useEffect(() => { load().catch(() => {}); }, []);

  // Map member_name (lowercase) → guest_id so each row links to the profile.
  const nameToGuestId = useMemo(() => {
    const m = {};
    guests.forEach((g) => { if (g.name) m[g.name.toLowerCase().trim()] = g.guest_id; });
    return m;
  }, [guests]);

  async function add(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/outfits", form);
      setForm(empty());
      setOpen(false);
      toast.success("Added to wedding party");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }

  async function patch(id, p) {
    await api.patch(`/outfits/${id}`, p);
    load();
  }
  async function remove(id) {
    if (!confirm("Remove this outfit?")) return;
    await api.delete(`/outfits/${id}`);
    load();
  }

  const totals = items.reduce((a, i) => ({
    cost: a.cost + (i.total_cost || 0),
    paid: a.paid + (i.paid_amount || 0),
  }), { cost: 0, paid: 0 });

  return (
    <div data-testid="dress-tux-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <p className="font-script text-[var(--gold)] text-3xl">Wardrobe</p>
          <h1 className="font-serif font-light text-4xl">Dress & Tux Tracker</h1>
          <p className="text-sm text-[var(--muted)] mt-2">Sizes, fittings, colors and payment for your entire party.</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="add-outfit-btn" className="btn-gold"><Plus className="w-4 h-4" /> Add Member</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Stat label="Members" v={items.length} />
        <Stat label="Total Cost" v={`$${totals.cost.toFixed(0)}`} />
        <Stat label="Paid" v={`$${totals.paid.toFixed(0)}`} />
        <Stat label="Outstanding" v={`$${(totals.cost - totals.paid).toFixed(0)}`} />
      </div>

      <div className="bg-white border border-[var(--line)] rounded-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">
            <tr><th className="px-5 py-3">Member</th><th className="px-5 py-3">Role</th><th className="px-5 py-3">Type</th><th className="px-5 py-3">Size</th><th className="px-5 py-3">Color</th><th className="px-5 py-3">Vendor</th><th className="px-5 py-3">Fitting</th><th className="px-5 py-3">Pickup</th><th className="px-5 py-3">Payment</th><th className="px-5 py-3"></th></tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const gid = nameToGuestId[(it.member_name || "").toLowerCase().trim()];
              return (
              <tr key={it.outfit_id} className="border-t border-[var(--line)] hover:bg-[#EDE3D3]/40 transition" data-testid={`outfit-row-${it.outfit_id}`}>
                <td className="px-5 py-4 font-medium">
                  {gid ? (
                    <Link to={`/dashboard/guests/${gid}`} className="text-[var(--ink)] hover:text-[var(--gold)] hover:underline flex items-center gap-1" data-testid={`outfit-member-link-${it.outfit_id}`}>
                      {it.member_name} <ChevronRight className="w-3 h-3 opacity-60" />
                    </Link>
                  ) : (
                    <span title="No matching guest — add this name to your guest list to enable the profile link">{it.member_name}</span>
                  )}
                </td>
                <td className="px-5 py-4 capitalize text-[var(--muted)]">{(ROLES.find(r=>r[0]===it.role)||[])[1]||it.role}</td>
                <td className="px-5 py-4 capitalize">{it.garment_type}</td>
                <td className="px-5 py-4">{it.size || "—"}</td>
                <td className="px-5 py-4">{it.color || "—"}</td>
                <td className="px-5 py-4">{it.vendor || "—"}</td>
                <td className="px-5 py-4 text-[var(--muted)]">{it.fitting_date || "—"}</td>
                <td className="px-5 py-4 text-[var(--muted)]">{it.pickup_date || "—"}</td>
                <td className="px-5 py-4">
                  <select value={it.payment_status} onChange={(e) => patch(it.outfit_id, { payment_status: e.target.value })} className="text-xs px-2 py-1 rounded-full bg-[var(--bg-warm)] outline-none">
                    {PAYMENT.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </td>
                <td className="px-5 py-4 text-right"><button onClick={() => remove(it.outfit_id)}><Trash2 className="w-4 h-4 text-[var(--muted)] hover:text-red-600" /></button></td>
              </tr>
            );
            })}
            {items.length === 0 && <tr><td colSpan="10" className="px-5 py-12 text-center text-[var(--muted)]"><Shirt className="w-8 h-8 mx-auto mb-3 text-[var(--gold)]" />Add your first wedding party member to start tracking outfits.</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <h3 className="font-serif text-2xl">Add Wedding Party Member</h3>
              <button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={add} className="grid grid-cols-2 gap-4">
              <Field label="Name" required value={form.member_name} onChange={(v) => setForm({...form, member_name: v})} testId="form-outfit-name" />
              <Field label="Role" select options={ROLES} value={form.role} onChange={(v) => setForm({...form, role: v})} />
              <Field label="Garment" select options={GARMENTS} value={form.garment_type} onChange={(v) => setForm({...form, garment_type: v})} />
              <Field label="Size" value={form.size} onChange={(v) => setForm({...form, size: v})} />
              <Field label="Shoe Size" value={form.shoe_size} onChange={(v) => setForm({...form, shoe_size: v})} />
              <Field label="Color" value={form.color} onChange={(v) => setForm({...form, color: v})} />
              <Field label="Vendor" value={form.vendor} onChange={(v) => setForm({...form, vendor: v})} />
              <Field label="Designer" value={form.designer} onChange={(v) => setForm({...form, designer: v})} />
              <Field label="Fitting Date" type="date" value={form.fitting_date} onChange={(v) => setForm({...form, fitting_date: v})} />
              <Field label="Pickup Date" type="date" value={form.pickup_date} onChange={(v) => setForm({...form, pickup_date: v})} />
              <Field label="Total Cost ($)" type="number" value={form.total_cost} onChange={(v) => setForm({...form, total_cost: parseFloat(v)||0})} />
              <Field label="Paid ($)" type="number" value={form.paid_amount} onChange={(v) => setForm({...form, paid_amount: parseFloat(v)||0})} />
              <div className="col-span-2"><Field label="Notes" value={form.notes} onChange={(v) => setForm({...form, notes: v})} /></div>
              <button type="submit" disabled={busy} className="btn-gold col-span-2" data-testid="form-outfit-submit">{busy ? "Adding…" : "Add Member"}</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, v }) {
  return (
    <div className="bg-white border border-[var(--line)] rounded-2xl p-5">
      <p className="font-serif text-3xl">{v}</p>
      <p className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)] mt-1">{label}</p>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, select, options = [], testId }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">{label}</label>
      {select ? (
        <select value={value} onChange={(e) => onChange(e.target.value)} className="input-luxe">
          {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      ) : (
        <input data-testid={testId} required={required} type={type} value={value} onChange={(e) => onChange(e.target.value)} className="input-luxe" />
      )}
    </div>
  );
}
